'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import Image from 'next/image';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { useRouter } from 'next/router';
import Caret from 'components/Icons/Caret';
import instagramService, { InstagramVideo } from 'services/instagram.service';
import InstagramVideoModal from 'components/InstagramVideo/InstagramVideoModal';
import { imageLoader } from 'functions/imageLoader';

interface IconProps {
  className?: string;
}

const InstagramIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const PlayIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
);

const CommunityShowcase: React.FC = () => {
  const [videos, setVideos] = useState<InstagramVideo[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const showcaseRef = useRef<HTMLDivElement>(null);

  const router = useRouter();

  const instagramAddress = useMemo(() => {
    return router.locale === 'ca' ? 'https://www.instagram.com/dresdenvisionca' : 'https://www.instagram.com/dresdenvision';
  }, [router.locale]);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (showcaseRef.current) {
      observer.observe(showcaseRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchVideos = async () => {
      setIsLoading(true);
      try {
        const fetchedVideos = await instagramService.getInstagramVideos(router.locale, 'home-page');
        setVideos(fetchedVideos);
      } catch (error) {
        console.error('Error fetching Instagram videos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideos();
  }, [router.locale]);

  const handleVideoClick = (index: number) => {
    setSelectedVideoIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (!isVisible) {
    return <div ref={showcaseRef} className="h-96 bg-gray-100"></div>;
  }

  if (isLoading || videos.length === 0) {
    return null;
  }

  return (
    <section ref={showcaseRef} className="bg-white py-[16px] px-4 sm:py-[24px]">
      <div className="mx-auto w-full max-w-[1224px]">

        <h1 className="text-3xl font-bold text-center my-2">Welcome to our community</h1>
        <p className="text-center mb-8">Good glasses for smart people</p>
        <div className="relative max-w-[1400px] mx-auto">
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={8}
            slidesPerView={2.5}
            navigation={{
              nextEl: '[data-reviews-slider-next]',
              prevEl: '[data-reviews-slider-previous]'
            }}
            pagination={{ clickable: true }}
            breakpoints={{
              640: {
                slidesPerView: 3,
                spaceBetween: 8,
              },
              768: {
                slidesPerView: 4,
                spaceBetween: 8,
              },
              1024: {
                slidesPerView: 5,
                spaceBetween: 8,
              },
              1280: {
                slidesPerView: 6,
                spaceBetween: 8,
              },
            }}
            className="relative"
          >
            {videos.map((video, index) => (
              <SwiperSlide key={video.id} className="!h-auto">
                <div className="relative group cursor-pointer w-full  overflow-hidden" onClick={() => handleVideoClick(index)}>
                  <Image
                    src={video.thumbnailUrl}
                    alt={video.title || `Video ${index + 1}`}
                    width={400}
                    height={100}
                    style={{
                      objectFit: "cover",
                    }}
                    className="w-full h-auto ob"
                    loader={imageLoader}
                  />
                  <div className="absolute inset-0 flex items-center justify-center lg:hidden z-10">
                    <PlayIcon className="w-12 h-12 text-white opacity-70 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity duration-300 hidden lg:flex items-center justify-center z-10">
                    <PlayIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
              </SwiperSlide>
            ))}

          </Swiper>
          <div
            data-reviews-slider-previous
            className="transition-color absolute left-[-68px] top-[120px] z-10 hidden h-[42px] w-[42px] rotate-90 transform cursor-pointer items-center justify-center rounded-full bg-white text-brand-black100 shadow-card duration-500 hover:text-brand-orange lg:flex">
            <Caret className="mt-[3px]" fill="currentColor" />
          </div>
          <div
            data-reviews-slider-next
            className="rotate absolute right-[-68px] top-[120px] hidden h-[42px] w-[42px]  -rotate-90 transform cursor-pointer items-center justify-center rounded-full bg-white text-brand-black100 shadow-card duration-500 hover:text-brand-orange lg:flex">
            <Caret className="mt-[3px]" fill="currentColor" />
          </div>
        </div>

        <div className="text-center mt-8">
          <a
            href={instagramAddress}
            className="inline-flex items-center text-[#00AEC7]"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <InstagramIcon className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm font-medium leading-5">Follow us on Instagram</span>
          </a>
        </div>
      </div>

      {/* Instagram Video Modal */}
      <InstagramVideoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        initialVideoIndex={selectedVideoIndex}
        videos={videos}
      />
    </section>
  );
};

export default CommunityShowcase;